import { LoadingIndicator } from 'components/common/LoadingIndicator';
import { Modal } from 'components/common/Modal/Modal';
import { DefaultAdRequestSourceManager } from 'core/adRequestSource/AdRequestSourceManager';
import { BidStrategy, L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import { Pmp } from 'core/pmp/Pmp';
import {
  DeliverType,
  RtbCampaign,
  RtbCampaignPlanType
} from 'core/rtbCampaign/RtbCampaign';
import { useCallAPI } from 'hooks/useCallAPI';
import { useCallback, useEffect, useMemo, useState } from 'react';
import i18n from 'i18n';
import SummaryDetail from 'components/SummaryDetail/SummaryDetail';
import { getPmpSummaryData } from 'containers/PmpOrder/PmpSummaryModel';
import { L1Object } from 'core/l1Object/L1Object';
import { renderOverBudgetWording } from 'containers/L2Objects/FormHintRenderFunctions';
import { DefaultAgencyManager } from 'core';
import styles from './pmpComparingModal.module.scss';
import { DefaultRtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';
import { Order } from 'core/order/Order';
import moment from 'moment';

type PmpComparingModalProps = {
  order: Order;
  l1Object: L1Object;
  campaignData: {
    id: number;
    pmp: Pmp;
    originPmp: Pmp;
    bidPrice: number;
  };
  onClose: () => void;
  onRefresh: () => void;
};

const adRequestSourceManager = new DefaultAdRequestSourceManager();
const agencyManager = new DefaultAgencyManager();
const campaignManager = new DefaultRtbCampaignManager();

export const PmpComparingModal: React.FC<PmpComparingModalProps> = ({
  order,
  l1Object,
  campaignData,
  onClose,
  onRefresh
}) => {
  const { loading, callAPIs } = useCallAPI();
  const { channelGroupId } = l1Object;
  const [pmpSpaceOptions, setPmpSpaceOptions] = useState<SelectOptions[]>([]);
  const [campaign, setCampaign] = useState<RtbCampaign>();

  useEffect(() => {
    const { id, pmp, originPmp } = campaignData;
    callAPIs(
      [
        () =>
          adRequestSourceManager.getSpacesWithConditions(
            true,
            {},
            'Outdoor',
            channelGroupId
          ),
        () => campaignManager.getCampaign(id),
        () => agencyManager.getAgencyName(pmp.adAgencyIds[0]),
        () => agencyManager.getAgencyName(originPmp.adAgencyIds[0])
      ],
      (pmpSpaceOptions: SelectOptions[], campaign: RtbCampaign) => {
        setPmpSpaceOptions(pmpSpaceOptions);
        setCampaign(campaign);
      }
    );
  }, [campaignData, channelGroupId, callAPIs]);

  const getPmpErrors = useCallback(
    (pmp: Pmp) => {
      if (!campaign) {
        return {};
      }
      const errors: { [key: string]: string | JSX.Element } = {};
      const availableBudget = l1Object.budgetBalance + campaign.basic.budget;
      if (pmp.budget > availableBudget) {
        errors.budget = renderOverBudgetWording(
          order.currency,
          availableBudget
        );
      }
      if (
        moment(pmp.startTime).isBefore(moment(order.startDate)) ||
        moment(pmp.endTime).startOf('day').isAfter(moment(order.endDate))
      ) {
        errors.dayRange = i18n.t(
          'campaign.descriptions.notWithinOrderDayRange'
        );
      }
      return errors;
    },
    [l1Object.budgetBalance, campaign, order]
  );

  const onSave = useCallback(() => {
    if (!campaign) {
      return;
    }
    const { pmp, bidPrice } = campaignData;
    const campaignBasic = {
      name: pmp.name,
      budget: pmp.budget,
      startDate: pmp.startTime,
      endDate: pmp.endTime,
      priceModel: RtbCampaignPlanType.FCPM,
      optimize: L2ObjectOptimizationGoal.IMPRESSIONS,
      orderPrice: bidPrice,
      bidPrice: bidPrice,
      deliverType: DeliverType.ACCELERATED,
      bidStrategy: BidStrategy.LOWEST_COST_WITH_BID_CAP,
      dayPart: pmp.dayPart
    };
    const newCampaign = {
      basic: {
        ...campaign.basic,
        ...campaignBasic
      },
      limitations: {
        exclude: [],
        preferred: [],
        nonPreferred: [],
        other: [
          {
            op: 'inc',
            type: 'dealId',
            value: [{ label: pmp.dealId, value: pmp.dealId }]
          }
        ],
        include: [
          {
            op: 'inc',
            type: 'adspace',
            value: pmp.spaces.map((space) => {
              const spaceOption = pmpSpaceOptions.find(
                (option) => option.value === space
              );
              return {
                label: spaceOption ? spaceOption.label : space,
                value: space
              };
            })
          }
        ]
      }
    };
    callAPIs(
      [
        () =>
          campaignManager.approvePmp(newCampaign, pmp.id, l1Object.l1ObjectId)
      ],
      () => {
        onClose();
        onRefresh();
      }
    );
  }, [
    campaign,
    pmpSpaceOptions,
    onClose,
    onRefresh,
    campaignData,
    l1Object.l1ObjectId,
    callAPIs
  ]);

  const onReject = useCallback(() => {
    callAPIs([() => campaignManager.rejectPmp(campaignData.pmp.id)], () => {
      onClose();
      onRefresh();
    });
  }, [campaignData.pmp.id, callAPIs, onClose, onRefresh]);

  const newPmpErrors = useMemo(() => {
    return getPmpErrors(campaignData.pmp);
  }, [campaignData.pmp, getPmpErrors]);

  const renderPmpDetail = useCallback(
    (
      pmp: Pmp,
      title: string,
      errors?: {
        [key: string]: string | JSX.Element;
      }
    ) => {
      const summaryData = getPmpSummaryData(
        pmp,
        pmpSpaceOptions,
        order.currency,
        errors
      );
      return (
        <div className={styles.pmpDetail}>
          <h4 className={styles.pmpTitle}>{title}</h4>
          <SummaryDetail data={summaryData} />
        </div>
      );
    },
    [pmpSpaceOptions, order.currency]
  );

  return (
    <div>
      {loading && <LoadingIndicator />}
      <Modal
        fullScreen={true}
        animation={false}
        title={i18n.t('pmpComparingModal.title')}
        primaryButton={{
          title: i18n.t('common.buttons.submit'),
          disabled: newPmpErrors && Object.keys(newPmpErrors).length > 0,
          callback: onSave
        }}
        secondaryButton={{
          title: i18n.t('common.buttons.cancel'),
          callback: onClose
        }}
        dangerButton={{
          title: i18n.t('common.buttons.reject'),
          variant: 'danger',
          callback: onReject
        }}
        dismiss={onClose}
        reverseFooter={true}
      >
        <div className={styles.pmpComparingContent}>
          {renderPmpDetail(
            {
              ...campaignData.originPmp,
              pmpType: campaignData.pmp.pmpType,
              channelGroupId: campaignData.pmp.channelGroupId
            },
            i18n.t('pmpComparingModal.labels.currentPmp')
          )}
          {renderPmpDetail(
            campaignData.pmp,
            i18n.t('pmpComparingModal.labels.newPmp'),
            newPmpErrors
          )}
        </div>
      </Modal>
    </div>
  );
};
